import { LocalStorageVariables } from "../../../../../common/entities/type";
import { DEFAULT_LANE_ORDER, ServiceLaneKey } from "./serviceLanes";
import { TimelinePeriod } from "./timeScale";
import { TimelineView } from "./TimelineToolbar";

/**
 * The Timeline's remembered state, per customer.
 *
 * The History tabs are routed children, so opening Assessments and coming
 * back unmounts the Timeline and every choice in it was lost; a reload did the
 * same. These five live in localStorage under one key, a map by customer id,
 * the way the listings' filters do (`common/recordTableFilterStore`): one key,
 * so logging out is a single `removeItem` — see the teardown lists in
 * `AuthProvider.logout` and `AppApiManager`, which both have to name it.
 *
 * Per customer and per browser: another device, or another affiliate, starts
 * from the defaults. Not stored: the anchor date (the Timeline opens on today)
 * and full screen.
 *
 * Reads validate every field and drop what they cannot use, so a stale or
 * hand-edited entry can never hide the chart. Selected keys are only checked
 * for shape here; `TimelineView` prunes the ones its catalog does not know.
 */
export interface TimelinePreferences {
  selectedKeys: string[];
  targetKeys: string[];
  view: TimelineView;
  period: TimelinePeriod;
  laneOrder: ServiceLaneKey[];
}

type StoredMap = Record<string, Partial<TimelinePreferences>>;

const VIEWS: TimelineView[] = ["combined", "chart", "events"];
const PERIODS: TimelinePeriod[] = ["week", "month", "year"];
// `group:id`, as `seriesKey` builds them.
const SERIES_KEY = /^(metric|biomarker|wearable):.+/;

const readMap = (): StoredMap => {
  try {
    const raw = localStorage.getItem(LocalStorageVariables.TIMELINE_PREFERENCES);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as StoredMap) : {};
  } catch {
    return {};
  }
};

const writeMap = (map: StoredMap) => {
  try {
    localStorage.setItem(
      LocalStorageVariables.TIMELINE_PREFERENCES,
      JSON.stringify(map)
    );
  } catch {
    // Out of quota or storage blocked — the Timeline just stops remembering.
  }
};

const seriesKeys = (value: unknown): string[] | undefined =>
  Array.isArray(value)
    ? value.filter((key): key is string => typeof key === "string" && SERIES_KEY.test(key))
    : undefined;

/** A lane order is usable only when it is the five lanes, each once. */
const laneOrder = (value: unknown): ServiceLaneKey[] | undefined => {
  if (!Array.isArray(value) || value.length !== DEFAULT_LANE_ORDER.length) return undefined;
  const keys = new Set(value);
  return DEFAULT_LANE_ORDER.every((key) => keys.has(key))
    ? (value as ServiceLaneKey[])
    : undefined;
};

/** What is remembered for `subjectId`: only the fields that validate. */
export const readTimelinePreferences = (
  subjectId: string | null | undefined
): Partial<TimelinePreferences> => {
  if (!subjectId) return {};
  const stored = readMap()[subjectId];
  if (!stored || typeof stored !== "object") return {};

  const result: Partial<TimelinePreferences> = {};
  const selectedKeys = seriesKeys(stored.selectedKeys);
  if (selectedKeys) result.selectedKeys = selectedKeys;
  const targetKeys = seriesKeys(stored.targetKeys);
  if (targetKeys) result.targetKeys = targetKeys;
  if (VIEWS.includes(stored.view as TimelineView)) result.view = stored.view;
  if (PERIODS.includes(stored.period as TimelinePeriod)) result.period = stored.period;
  const order = laneOrder(stored.laneOrder);
  if (order) result.laneOrder = order;
  return result;
};

/** Remembers the Timeline's state for `subjectId`. */
export const writeTimelinePreferences = (
  subjectId: string | null | undefined,
  preferences: TimelinePreferences
) => {
  if (!subjectId) return;
  const map = readMap();
  map[subjectId] = preferences;
  writeMap(map);
};
